/**
 * This reference template is designed to showcase the elements used to construct your own
 * application.
 * 
 * When developing take care to:
 *  - Retain user interaction to begin audio.
 *  - Understand video sizing and mobile screen orientation.
 
 * See attached documentation for reference. Contact support@pureweb.com with any questions.
 * 
 *
 * Copyright (C) PureWeb 2020
 */

import { StreamerStatus } from "@pureweb/platform-sdk";
import {
  useStreamer,
  useLaunchRequest,
  System,
} from "@pureweb/platform-sdk-react";
import React, { useEffect, useState } from "react";

// Components
import { AppView } from "./components/AppView/AppView";
import { AppWrapperComponent } from "./components/AppWrapper/AppWrapper";
import { ConfigurationError } from "./components/ConfigurationError/ConfigurationError";

// Hooks
import { useDownloadManager } from "./hooks/useDownloadManager";
import { useModelManager } from "./hooks/useModelManager";
import { usePlatform } from "./hooks/usePlatform";

// Utils and Services
import { ClientOptionsValidator } from "./utils";
import { GameMessage } from "./types";

// Styles and Config
import "./App.css";
import logger from "./Log";

var Controller = require("./controller.js");

const App: React.FC = () => {
  const {
    platform,
    clientOptions,
    audio,
    streamerOptions,
    launchRequestOptions,
    client,
  } = usePlatform();

  const {
    modelDefinitionUnavailable,
    modelDefinition,
    availableModels,
    initializeModels,
  } = useModelManager(clientOptions);

  const {
    showDownloadDialog,
    downloadInfo,
    isLoadingAssetInfo,
    handleConfirmDownload,
    handleCancelDownload,
    processDownloadMessage,
  } = useDownloadManager();

  const [status, launchRequest, queueLaunchRequest] = useLaunchRequest(
    platform,
    modelDefinition,
    launchRequestOptions
  );

  const [streamerStatus, emitter, videoStream, audioStream, messageSubject] =
    useStreamer(platform, launchRequest, streamerOptions);

  const [loading, setLoading] = useState(false);

  // Initialize models and setup
  useEffect(() => {
    const initializeAsync = async () => {
      console.log("Initializing models with options:", clientOptions);
      if (clientOptions.ProjectId) {
        try {
          streamerOptions.forceRelay = clientOptions.ForceRelay;
          await initializeModels(platform);
          if (platform.agent?.serviceCredentials?.iceServers) {
            streamerOptions.iceServers = platform.agent.serviceCredentials
              .iceServers as RTCIceServer[];
          }
        } catch (err) {
          logger.error(err);
        }
      }
    };

    initializeAsync();
  }, [clientOptions, initializeModels, platform, streamerOptions]);

  // Setup audio stream
  useEffect(() => {
    if (audioStream) {
      audio.srcObject = audioStream;
    }
  }, [audioStream, audio]);

  // Handle streamer status changes
  useEffect(() => {
    if (streamerStatus === StreamerStatus.Failed) {
      platform.disconnect();
    }
  }, [streamerStatus, platform]);

  // Log status changes
  useEffect(() => {
    logger.info("Status", status, streamerStatus);
  }, [status, streamerStatus]);

  // Subscribe to game messages
  useEffect(() => {
    const subscription = messageSubject.subscribe(
      (value: string) => {
        logger.info("Message: " + value);
        try {
          const message: GameMessage = JSON.parse(value);
          processDownloadMessage(message);
        } catch (err) {
          logger.error("Error parsing message: ", err);
        }
      },
      (err: any) => {
        logger.error(err);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [messageSubject, processDownloadMessage]);

  // Debug controller
  useEffect(() => {
    console.log(Controller);
  }, []);

  const launch = async () => {
    setLoading(true);
    audio.load();

    if (clientOptions.LaunchType !== "local") {
      await queueLaunchRequest();
    }
  };

  // Configuration validation
  if (!ClientOptionsValidator.isValid(clientOptions)) {
    return (
      <AppWrapperComponent
        showDownloadDialog={showDownloadDialog}
        downloadInfo={downloadInfo}
        onConfirmDownload={handleConfirmDownload}
        onCancelDownload={handleCancelDownload}
        isLoadingAssetInfo={isLoadingAssetInfo}
      >
        <ConfigurationError clientOptions={clientOptions} />
      </AppWrapperComponent>
    );
  }

  // Main app view
  return (
    <AppWrapperComponent
      showDownloadDialog={showDownloadDialog}
      downloadInfo={downloadInfo}
      onConfirmDownload={handleConfirmDownload}
      onCancelDownload={handleCancelDownload}
      isLoadingAssetInfo={isLoadingAssetInfo}
    >
      <AppView
        client={client}
        clientOptions={clientOptions}
        streamerStatus={streamerStatus}
        status={status}
        loading={loading}
        modelDefinitionUnavailable={modelDefinitionUnavailable}
        availableModels={availableModels}
        videoStream={videoStream}
        emitter={emitter}
        onLaunch={launch}
      />
    </AppWrapperComponent>
  );
};

const BrowserSupportWrapper: React.FC = () => {
  return System.IsBrowserSupported() ? (
    <App />
  ) : (
    <div className="ui red segment center aligned basic">
      <h2 className="header">Your browser is currently unsupported</h2>
    </div>
  );
};

export default BrowserSupportWrapper;
